/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import React from 'react'
import styled from "styled-components";

interface IArgosFlexbox {
  rowGap?: number
  colGap?: number
  noGap?: boolean
  display?: string,
  vertical?: boolean
  wrap?: boolean,
  alignItems?: string,
  justifyContent?: string
}

/**
 * defaults:
 * rowGap: 0
 * colGap: 0
 * flexDirection: row
 * flexWrap: nowrap
 *
 * This is a wrapper around css flexbox. Use it for displaying elements in a list. By default, elements inside it will display
 * as a horizontal list with space between them as 10px (defined by colGap). If you want to make the list vertical, provide
 * the "vertical" prop. If you want the items to wrap on overflow, provide the "$wrap" prop. You use the "$" because
 * wrap could be supplied as a string to a div, but this is specifying you don't want that. It might work anyway but you'll see a console error.
 * By default, wrap is false.
 *
 * You can also provide props as you would a normal div, such as using style={{}} for any props the component does not define.
 *
 * To align the items, see the css documentation for justifyContent and alignItems. justifyContent works along main axis,
 * and alignItems on the other. For a horizontal list, the main axis is horizontal, so "justifyContent=center" will align the
 * items in the center of the horizontal axis. In this case, "alignItems=center" would align the items in the center of the vertical axis.
 * For a vertical list, it works opposite because the main axis is vertical. So justifyContent still applies to the main axis
 * which is now vertical, thus "justifyContent=center" would align the items in the center of the vertical axis.
 *
 */
const ArgosFlexbox = styled.div<IArgosFlexbox>((props) => {
  const {rowGap, colGap, noGap} = props
  return (
    {
      display: props?.display ?? 'flex',
      rowGap: noGap ? 'initial' : rowGap ?? 10,
      columnGap: noGap ? 'initial' : colGap ?? 10,
      flexDirection: props?.vertical ? 'column' : 'row',
      flexWrap: props?.wrap ? 'wrap' : 'nowrap',
      alignItems: props?.alignItems ?? 'initial',
      justifyContent: props?.justifyContent ?? 'initial'
    }
  )
})

export default ArgosFlexbox